import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  Chip,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  FitnessCenter as FitnessCenterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useReportComparison } from "../../../../../contexts/ReportComparisonContext";
import { compareExercisePerformance } from "../../../../../utils/comparisonUtils";
import ComparisonDisplay from "../../../../../components/ComparisonDisplay";
import { useExercisesBigQuery } from "../../../../../hooks/useExercisesBigQuery";

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  marginBottom: theme.spacing(3),
  overflow: "visible",
}));

const CardContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

const WorkoutItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  transition: "all 0.2s",
  "&:hover": {
    boxShadow: theme.shadows[2],
  },
}));

const ExerciseCard = ({ report }) => {
  const [expandedWorkout, setExpandedWorkout] = React.useState(null);
  const { isComparisonEnabled, selectedPreviousReport } = useReportComparison();

  // Fetch exercises from BigQuery for name lookups
  const { data: exercisesData, isLoading: exercisesLoading } =
    useExercisesBigQuery({
      search: "",
      type: "all",
      limit: 1000,
      offset: 0,
      enabled: true,
    });

  // Extract exercises array from BigQuery response
  const bigQueryExercises = exercisesData?.data?.exercises || [];

  // Helper function to get exercise name by ID from BigQuery data
  const getExerciseNameById = React.useMemo(() => {
    return (exerciseId) => {
      if (!exerciseId) return "Unknown Exercise";

      // First try to find in BigQuery exercises
      const bigQueryExercise = bigQueryExercises.find(
        (ex) => ex.id === exerciseId,
      );
      if (bigQueryExercise) {
        return bigQueryExercise.name;
      }

      // Last resort - format the ID as a readable name
      const formattedName = exerciseId
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      return formattedName;
    };
  }, [bigQueryExercises]);

  if (!report || !report.exerciseData) return null;

  const handleToggleWorkout = (workoutId) => {
    setExpandedWorkout(expandedWorkout === workoutId ? null : workoutId);
  };

  // Sort exercise data by date
  const sortedExercises = [...report.exerciseData].sort((a, b) => {
    const dateA = new Date(a.createdOn.seconds * 1000);
    const dateB = new Date(b.createdOn.seconds * 1000);
    return dateB - dateA; // Most recent first
  });

  // Get comparison data if comparison is enabled
  const comparisonData = React.useMemo(() => {
    if (!isComparisonEnabled || !selectedPreviousReport?.exerciseData) {
      return null;
    }
    return compareExercisePerformance(
      report.exerciseData,
      selectedPreviousReport.exerciseData,
    );
  }, [isComparisonEnabled, selectedPreviousReport, report.exerciseData]);

  return (
    <StyledCard>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FitnessCenterIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              Exercise Summary
            </Typography>
          </Stack>
        }
      />
      {/* Comparison Display */}
      {isComparisonEnabled && comparisonData && (
        <Box sx={{ px: 3, pb: 2 }}>
          <ComparisonDisplay data={comparisonData} type="exercise" />
        </Box>
      )}
      <CardContentWrapper>
        {sortedExercises.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              No exercise data recorded for this period
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {sortedExercises.length} workout
              {sortedExercises.length > 1 ? "s" : ""} completed during this
              reporting period
            </Typography>
            <List disablePadding>
              {sortedExercises.map((workout) => {
                const isCompleted = workout.status === "complete";
                const workoutDate = new Date(workout.createdOn.seconds * 1000);
                const isExpanded = expandedWorkout === workout.id;

                return (
                  <React.Fragment key={workout.id}>
                    <WorkoutItem
                      disablePadding
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleToggleWorkout(workout.id)}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {workout.reportData?.split?.name || "Workout"}
                            </Typography>
                            {isCompleted && (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label="Completed"
                                color="success"
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {workoutDate.toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })}
                            {workout.reportData?.protocol?.name && (
                              <> • {workout.reportData.protocol.name}</>
                            )}
                          </Typography>
                        }
                        sx={{ py: 1, px: 2 }}
                      />
                    </WorkoutItem>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
                        {/* Protocol details */}
                        {workout.reportData?.protocol?.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2, fontStyle: "italic" }}
                          >
                            {workout.reportData.protocol.description}
                          </Typography>
                        )}

                        {/* Exercises */}
                        {workout.reportData?.exercises?.length > 0 ? (
                          <Grid container spacing={1}>
                            {workout.reportData.exercises.map(
                              (exercise, index) => (
                                <Grid
                                  item
                                  xs={12}
                                  md={6}
                                  key={exercise.id || index}
                                >
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      bgcolor:
                                        exercise.clientAdded === true
                                          ? "#FFF8E1"
                                          : exercise.swappedFromExerciseId
                                            ? "#F0FDF4"
                                            : "background.paper",
                                      borderRadius: 1,
                                      border: "1px solid",
                                      borderColor:
                                        exercise.clientAdded === true
                                          ? "warning.light"
                                          : exercise.swappedFromExerciseId
                                            ? "success.light"
                                            : "divider",
                                    }}
                                  >
                                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: "wrap", mb: 0.25 }}>
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight="bold"
                                    >
                                      {exercisesLoading
                                        ? "Loading..."
                                        : exercise.exerciseName
                                          ? exercise.exerciseName
                                          : getExerciseNameById(
                                              exercise.exercise ||
                                                exercise.exerciseId ||
                                                exercise.id,
                                            )}
                                    </Typography>
                                    {exercise.swappedFromExerciseId && (
                                      <Chip label="Swapped" size="small" color="success" variant="outlined" />
                                    )}
                                    {exercise.clientAdded === true && (
                                      <Chip label="Client Added" size="small" color="warning" variant="outlined" />
                                    )}
                                    </Stack>
                                    {exercise.swappedFromExerciseId && (
                                      <Typography variant="caption" color="success.main" sx={{ display: "block", mb: 0.5 }}>
                                        Swapped from: {exercise.swappedFromExerciseName || getExerciseNameById(exercise.swappedFromExerciseId)}
                                      </Typography>
                                    )}

                                    {exercise.type === "super_set" ? (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Super Set:{" "}
                                        {exercise.superSetName ||
                                          "Multiple Exercises"}
                                      </Typography>
                                    ) : (
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{ mt: 0.5 }}
                                      >
                                        {exercise.sets && (
                                          <Chip
                                            label={`${exercise.sets.length} Sets`}
                                            size="small"
                                            variant="outlined"
                                          />
                                        )}
                                        {exercise.completed && (
                                          <Chip
                                            label="Completed"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                          />
                                        )}
                                      </Stack>
                                    )}
                                  </Box>
                                </Grid>
                              ),
                            )}
                          </Grid>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                          >
                            No exercise details available
                          </Typography>
                        )}
                      </Box>
                    </Collapse>

                    {/* Add a small divider between workout items */}
                    {sortedExercises.indexOf(workout) <
                      sortedExercises.length - 1 && (
                      <Box sx={{ my: 1 }}>
                        <Divider />
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          </>
        )}
      </CardContentWrapper>
    </StyledCard>
  );
};

export default ExerciseCard;
